///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, OnDestroy, OnInit } from '@angular/core';
import { GtthHistoryService } from '../logical-topology/engine/history-service';
import { Subject, takeUntil } from 'rxjs';

import { ActionsToolbarModeEnum, GtthStepGroupEnum } from '../../generated/shared-types.model';
import { GtthChangeStepCommand } from '../logical-topology/engine/commands/toolbar/change-step-command';
import { GtthEventManager } from '../../services/event-manager.service';
import { GtthInfoWindowsService } from '../logical-topology/map-entities-info/info-windows-service';
import { GtthProjectStateService } from '../logical-topology/engine/project-state.service';

interface StepToolbarModel {
    readonly id: number;
    readonly step: GtthStepGroupEnum;
    readonly title: string;
    readonly stepAdditionalStyle: string;
    keepOpen: boolean;
    collapse: boolean;
}

@Component({
    selector: 'winde-steps-toolbar',
    templateUrl: './steps-toolbar.component.html',
    styleUrls: ['./steps-toolbar.scss'],
    standalone: false
})
export class GtthStepsToolbarComponent implements OnInit, OnDestroy {
    public selectedStepNumber: GtthStepGroupEnum;
    public readonly stepsList: StepToolbarModel[] = [
        {
            id: 1,
            step: GtthStepGroupEnum.LOCATIONS_AND_LOSES,
            title: 'Enter Locations',
            keepOpen: false,
            collapse: true,
            stepAdditionalStyle: 'steps-toolbar__item--locations'
        } as StepToolbarModel,
        {
            id: 2,
            step: GtthStepGroupEnum.LOCATIONS_AND_LOSES,
            title: 'LOS',
            keepOpen: false,
            collapse: true,
            stepAdditionalStyle: 'steps-toolbar__item--los'
        } as StepToolbarModel,
        {
            id: 3,
            step: GtthStepGroupEnum.PLAN_AND_OPTIMISE,
            title: 'Design/Optimize',
            keepOpen: false,
            collapse: true,
            stepAdditionalStyle: 'steps-toolbar__item--plan'
        } as StepToolbarModel,
        {
            id: 4,
            step: GtthStepGroupEnum.PLAN_AND_OPTIMISE,
            title: 'Reports',
            keepOpen: false,
            collapse: true,
            stepAdditionalStyle: 'steps-toolbar__item--report'
        } as StepToolbarModel,
        {
            id: 5,
            step: GtthStepGroupEnum.BOM,
            title: 'Export',
            keepOpen: false,
            collapse: true,
            stepAdditionalStyle: 'steps-toolbar__item--bom'
        } as StepToolbarModel
    ];

    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private readonly eventManager: GtthEventManager,
        public readonly historyService: GtthHistoryService,
        public readonly infoWindowsService: GtthInfoWindowsService,
        private readonly projectStateService: GtthProjectStateService
    ) {}

    ngOnInit() {
        this.selectedStepNumber = this.historyService.selectedStepNumber;
        this.stepKeepOpened(this.historyService.toolbarMode);

        this.eventManager
            .getEventSubscription('actionModeChanged')
            .pipe(takeUntil(this.destroy$))
            .subscribe((newMode) => this.stepKeepOpened(newMode.content));
        this.eventManager
            .getEventSubscription('changeProjectStep')
            .pipe(takeUntil(this.destroy$))
            .subscribe((content) => this.stepChanged(-1, content.stepNumber));
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    modeChanged(data: { type: ActionsToolbarModeEnum; terminalLayer?: number }) {
        this.historyService.modeChanged(data);
        this.stepKeepOpened(data.type);
    }

    stepKeepOpened(type: ActionsToolbarModeEnum) {
        let keepOpenEl = -1;
        switch (type) {
            case ActionsToolbarModeEnum.TERMINAL:
                keepOpenEl = 0;
                break;
            case ActionsToolbarModeEnum.NO_LOS:
            case ActionsToolbarModeEnum.FIBER:
            case ActionsToolbarModeEnum.CONFIRMED_LOS:
            case ActionsToolbarModeEnum.LINK:
            case ActionsToolbarModeEnum.NOT_CONFIRMED_LOS:
                keepOpenEl = 1;
        }

        this.stepsList.forEach((el, index) => {
            el.keepOpen = index === keepOpenEl;
        });
    }

    collapseStep(step, state: boolean) {
        const stepTooltipSelector = `sui-tooltip > div[data-related-container-ref="div.step--${step.id}"]`;
        const frozenTooltipsInside = document.querySelectorAll(stepTooltipSelector);

        if (state && frozenTooltipsInside.length) {
            return;
        }

        if (this.checkSteepDisabled(step.id)) {
            step.collapse = true;
        } else if (step.step === this.selectedStepNumber) {
            step.collapse = state;
        }
    }

    stepChanged(stepId: number, stepNumber: GtthStepGroupEnum) {
        if (this.checkSteepDisabled(stepId)) {
            return;
        }

        if (this.selectedStepNumber === stepNumber) {
            this.historyService.closeInfoWindowAndArea();
            return;
        }

        this.selectedStepNumber = stepNumber;

        this.stepsList.forEach((stepObject) => {
            if (stepObject.step === this.selectedStepNumber) {
                stepObject.collapse = stepObject.id !== stepId;
            } else {
                stepObject.collapse = true;
            }
        });

        this.historyService.doCommandBypassHistory(this.historyService.command(GtthChangeStepCommand).create(this.selectedStepNumber));
    }

    checkSteepDisabled(stepID: number): boolean {
        if (this.projectStateService.isPotentiallyCorruptedProject()) {
            // all steps are enabled for potentially corrupted projects
            return false;
        } else if (this.projectStateService.isAreaPlannerProject()) {
            // currently Reports and BOM steps are disabled for Area Planner projects
            return stepID === 4 || stepID === 5;
        } else if (this.projectStateService.isBomConfigProject()) {
            // BOM config - disable everything except BOM
            return stepID === 1 || stepID === 2 || stepID === 3 || stepID === 4;
        } else {
            // regular project - depends on project state
            return (
                (stepID === 4 || stepID === 5) &&
                (!this.historyService.logicalTopology.isShowPlanningMode || this.historyService.logicalTopology.isBomStepDisabled)
            );
        }
    }

    trackId(_index: number, item: any) {
        return item.id;
    }
}
